import React from 'react'
import Dropdown from '../Dropdown'
import { RADOM_COLORS } from '../../util/Constants'
import { NotificationBell } from '../../icons/NotificationBell'
import EventsState from '../../state/Events'
import { useNavigate } from 'react-router-dom'
import { formatDistance } from 'date-fns'
import { getTransactionReference, getNotificationItem, Notification, NotificationAmount, NotificationBody, NotificationDetailPill, NotificationDetails, NotificationDropdownShell, NotificationEmpty, NotificationHeader, NotificationHeaderCount, NotificationHeaderTitle, NotificationIconBadge, NotificationList, NotificationMeta, NotificationMethodLogo, NotificationTitle, NotificationTitleRow, NotificationUnreadDot, NotificationWhen } from './notification-utils'
export interface NotificationDropdownProps {
  onClickNotifications: () => void
  latestBlockAcks: { [chainID: number]: number }
  setLatestBlockAcks: (latestBlockAcks: { [chainID: number]: number }) => void
  latestManagedAck: Date
  setLatestManagedAck: (latestManagedAck: Date) => void
  setNotificationsCloseFn: (fn: () => void) => void
  notificationsCloseFn: () => void
  numNotifications: number
}

const NotificationDropdown = ({
  onClickNotifications,
  latestBlockAcks, setLatestBlockAcks,
  latestManagedAck, setLatestManagedAck,
  setNotificationsCloseFn, notificationsCloseFn,
  numNotifications
}: NotificationDropdownProps): JSX.Element => {
  const navigate = useNavigate()

  return (
    <Dropdown
      onClick={onClickNotifications}
      transparent
      noChevron
      preferredAlignment='right'
      innerStyle={{ maxHeight: '75vh' }}
      onClose={() => {
        if (
          Object.entries(EventsState.latestBlockScanned)
            .some(([chainID, latestBlockScanned]) =>
              latestBlockScanned > latestBlockAcks[+chainID]
            )
        ) {
          setLatestBlockAcks(EventsState.latestBlockScanned)
        }
        setLatestManagedAck(new Date())
      }}
      onCloseFn={fn => setNotificationsCloseFn(() => fn)}
      selectedContent={
        <div style={{ position: 'relative' }}>
          {
            numNotifications > 0 && <div style={{
              padding: 4,
              borderRadius: '100%',
              backgroundColor: RADOM_COLORS.ORANGE,
              border: '2px solid white',
              color: '#FFF',
              fontWeight: 600,
              fontSize: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              top: -5,
              right: -5
            }} />
          }
          <NotificationBell style={{ height: 18, width: 18 }} stroke={RADOM_COLORS.GRAY_DARKER} strokeWidth="2" />
        </div>
      }
      dropdownContent={
        <NotificationDropdownShell>
          <NotificationHeader>
            <NotificationHeaderTitle>Notifications</NotificationHeaderTitle>
            {EventsState.allEvents.length > 0 && (
              <NotificationHeaderCount>
                {EventsState.allEvents.length === 1 ? '1 event' : `${EventsState.allEvents.length} events`}
              </NotificationHeaderCount>
            )}
          </NotificationHeader>
          {
            EventsState.allEvents.length <= 0
              ? <NotificationEmpty>No notifications yet</NotificationEmpty>
              : <NotificationList>
                {
                  EventsState.allEvents.slice(0, 100).map((e, i) => {
                    const notification = getNotificationItem(e, latestManagedAck)
                    return <Notification key={`${e.name}-${getTransactionReference(e) ?? i}`} onClick={() => {
                      navigate(notification.url)
                      setLatestBlockAcks(EventsState.latestBlockScanned)
                      notificationsCloseFn()
                    }}>
                      <NotificationIconBadge $tone={notification.tone}>
                        {notification.unread && <NotificationUnreadDot />}
                        {notification.icon}
                      </NotificationIconBadge>
                      <NotificationBody>
                        <NotificationTitleRow>
                          <NotificationTitle>{notification.title}</NotificationTitle>
                          <NotificationWhen>
                            {formatDistance(e.date, new Date(), { addSuffix: true })}
                          </NotificationWhen>
                        </NotificationTitleRow>
                        <NotificationMeta>
                          {notification.methodLogo && (
                            <NotificationMethodLogo src={notification.methodLogo} alt='' />
                          )}
                          {notification.meta}
                        </NotificationMeta>
                        {notification.details.length > 0 && <NotificationDetails>
                          {notification.details.map(detail =>
                            <NotificationDetailPill key={detail}>
                              {detail}
                            </NotificationDetailPill>
                          )}
                        </NotificationDetails>}
                        {notification.amountLabel && (
                          <NotificationAmount $tone={notification.tone}>
                            {notification.amountLabel}
                          </NotificationAmount>
                        )}
                      </NotificationBody>
                    </Notification>
                  })
                }
              </NotificationList>
          }
        </NotificationDropdownShell>
      }
    />
  )
}

export default NotificationDropdown
